import {
  useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExSideDrawer,
  ButtonType,
  ButtonSize,
  ExButton,
  ExInput,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ButtonFlavor,
  SearchSize,
  SearchFlavor,
  ExIcon,
  IconVariant,
  ExTable,
  ExCard,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
  ExBadge,
  BadgeColor,
  BadgeShape,
  BadgeSize,
  ExAlertBanner,
  AlertBannerType,
  AlertBannerVariant,
  ExEmptyState,
} from '@boomi/exosphere';
import { createRoot } from 'react-dom/client';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import './ModelsListDrawer.scss';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import { providerLogo, ModelAgentsDrawer } from '@components/ModelsManagement';
import { handleError } from '@services/agent-foundation/agent-foundation-api-service';

interface ModelsListDrawerProps {
  open: boolean;
  configName?: string;
  configId?: string;
  provider?: string;
  navigationEnabled?: boolean;
  onClose: () => void;
  onParentClose?: () => void;
  inline?: boolean;
  onShowAgents?: (model: any) => void;
  fromConfigurationsDrawer?: boolean;
  initialModels?: any[];
}

function ModelsListDrawer({
  open,
  configName = '',
  configId = '',
  provider = 'Custom',
  navigationEnabled = false,
  onClose,
  inline = false,
  onShowAgents,
  fromConfigurationsDrawer = false,
  initialModels = undefined,
  onParentClose,
}: ModelsListDrawerProps) {
  const [t] = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [filteredModels, setFilteredModels] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [gridApi, setGridApi] = useState<any>(null);
  const useCardView = true;
  const [showBoomiModelsDrawer, setShowBoomiModelsDrawer] = useState(false);
  const [selectedModelForAgents, setSelectedModelForAgents] = useState<any>(null);
  const totalRowsRef = useRef(0);

  useEffect(() => {
    if (open) {
      // If initialModels are provided, use them directly
      if (initialModels && initialModels.length > 0) {
        setModels(initialModels);
        setFilteredModels(initialModels);
        totalRowsRef.current = initialModels.length;
      } else {
        // Otherwise, fetch from API
        setIsLoading(true);
        modelsApiService.getModelsList(configId)
          .then((res: any) => {
            if (!res?.success) {
              handleError(res, 'Error while getting model configuration details');
              setIsLoading(false);
              return;
            }
            if (res?.success && res?.data) {
              setModels(res.data.models);
              setFilteredModels(res.data.models);
              totalRowsRef.current = res.data.models.length;
            } else {
              setModels([]);
              setFilteredModels([]);
              totalRowsRef.current = 0;
            }
            setIsLoading(false);
          })
          .catch(() => {
            setModels([]);
            totalRowsRef.current = 0;
            setIsLoading(false);
          });
      }
    }
  }, [open, configId, initialModels]);

  useEffect(() => {
    const filtered = models.filter((model) => {
      const lowerSearch = searchTerm.toLowerCase();
      return model.display_name?.toLowerCase().includes(lowerSearch)
        || model.id?.toLowerCase().includes(lowerSearch);
    });
    setFilteredModels(filtered);
    totalRowsRef.current = filtered.length;
    setSelectedPage(1);
  }, [searchTerm, models]);

  const hasDeprecatedModel = models.some(
    (model) => model.deployment_count > 0 && model.status?.toLowerCase() === 'deprecated',
  );
  const hasRetiredModel = models.some(
    (model) => model.deployment_count > 0 && model.status?.toLowerCase() === 'retired',
  );

  const modelNameCellRenderer = (params: any) => {
    const container = document.createElement('div');
    const root = createRoot(container);

    root.render(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        height: '56px',
      }}
      >
        <div style={{ marginLeft: '8px', overflow: 'hidden' }}>
          <div style={{
            color: 'var(--exo-color-font)',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '15px',
          }}
          >
            {params.data.display_name}
          </div>
          <div style={{
            color: 'var(--exo-color-font-secondary)',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '15px',
          }}
          >
            ID:
            {' '}
            {params.data.id}
          </div>
        </div>
      </div>,
    );
    return container;
  };

  const renderModelCard = (model: any) => {
    const logo = providerLogo(model.provider || 'Custom');
    const agentCountStyle: React.CSSProperties = {
      background: 'none',
      border: 'none',
      padding: '0',
      color: 'var(--exo-color-font-link)',
      fontFamily: 'var(--exo-font-family)',
      fontWeight: 400,
      fontSize: '14px',
      cursor: 'pointer',
      textDecoration: 'underline',
    };

    return (
      <ExCard key={model.id} data-testid="models-list-drawer__card">
        <div className="models-list-drawer__card-header">
          <div className="models-list-drawer__card-icon-wrapper">
            <img
              src={logo.src}
              alt={logo.alt}
              className="models-list-drawer__card-icon"
            />
          </div>
          <div className="models-list-drawer__card-title">{model.display_name}</div>
          {(model.status?.toLowerCase() === 'deprecated'
            || model.status?.toLowerCase() === 'retired') && (
            <ExBadge
              className="register-model-drawer__status-badge"
              color={
                model.status?.toLowerCase() === 'deprecated'
                  ? BadgeColor.YELLOW
                  : BadgeColor.RED
              }
              shape={BadgeShape.SQUARED}
              size={BadgeSize.SMALL}
              data-testid={`model-list-${model.model_id}-status-badge`}
            >
              {t(
                model.status?.toLowerCase() === 'deprecated'
                  ? 'models_badge_deprecated'
                  : 'models_badge_retired',
              )}
            </ExBadge>
          )}
        </div>
        <div className="models-list-drawer__card-content">
          <div className="models-list-drawer__card-field">
            <span className="models-list-drawer__card-label">{t('model_description')}</span>
            <ExTooltip
              tooltipDelay={0}
              position={TooltipPosition.TOP}
              alignment={TooltipAlignment.START}
            >
              <div>{model.description || 'No description available'}</div>
              <p
                slot="anchor"
                className="models-list-drawer__card-description"
              >
                {model.description || 'No description available'}
              </p>
            </ExTooltip>
          </div>
          <div className="models-list-drawer__card-field">
            <span className="models-list-drawer__card-label">{t('deployed_agents')}</span>
            {(model.deployment_count || 0) > 0 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (inline && onShowAgents) {
                    onShowAgents(model);
                  } else if (!inline) {
                    setSelectedModelForAgents(model);
                    setShowBoomiModelsDrawer(true);
                  }
                }}
                style={agentCountStyle}
              >
                {model.deployment_count || 0}
              </button>
            ) : (
              <span style={{
                color: 'var(--exo-color-font-secondary)',
                fontFamily: 'var(--exo-font-family)',
                fontWeight: 400,
                fontSize: '12px',
              }}
              >
                {model.deployment_count || 0}
              </span>
            )}
          </div>
        </div>
      </ExCard>
    );
  };

  const columnDefs = [
    {
      field: 'name',
      headerName: 'Model Name',
      flex: 1,
      cellRenderer: (params: any) => modelNameCellRenderer(params),
      resizable: true,
    },
    {
      field: 'deployedAgents',
      headerName: 'Deployed Agents',
      flex: 0.5,
      resizable: true,
    },
  ];

  const [gridOptions] = useState<any>({
    headerHeight: 36,
    defaultColDef: {
      resizable: true,
      sortable: false,
      cellStyle: { fontSize: '14px', fontFamily: 'Noto Sans' },
    },
    columnDefs,
    rowData: [],
    domLayout: 'autoHeight',
    suppressPaginationPanel: true,
    rowHeight: 56,
    popupParent: document.querySelector('body'),
    suppressDragLeaveHidesColumns: true,
    suppressMovableColumns: true,
    onGridReady: (event: any) => {
      setGridApi(event.api);
    },
  });

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('rowData', filteredModels);
    }
  }, [gridApi, filteredModels, selectedPage]);

  const handleClose = () => {
    onClose();
    onParentClose?.();
  };

  const renderContent = () => (
    <>
      {isLoading && (
        <div className="models-list-drawer__loader">
          <ExLoader
            size={LoaderSize.MEDIUM}
            label={t('ai_loading')}
            variant={LoaderVariant.SPINNER}
          />
        </div>
      )}

      {!isLoading && (
        <div className="models-list-drawer__body">
          {configName && !inline && (
            <div className="models-list-drawer__model-info">
              <div className="models-list-drawer__model-icon-wrapper">
                <img
                  src={providerLogo(provider).src}
                  alt={providerLogo(provider).alt}
                  className="models-list-drawer__model-icon"
                />
              </div>
              <span className="models-list-drawer__model-name">{configName}</span>
            </div>
          )}
          {!inline && (
            <div className="models-list-drawer__search-wrapper">
              <ExInput
                placeholder={t('search_by_model_name', 'Search by model name')}
                data-testid="models-list-search"
                type="text"
                clearable
                size={SearchSize.MEDIUM}
                flavor={SearchFlavor.WHITE}
                onInput={(e: any) => {
                  setSearchTerm(e.target.value);
                }}
              >
                <ExIcon icon="Search" variant={IconVariant.ICON} slot="prefix" />
              </ExInput>
            </div>
          )}

          {!inline && hasDeprecatedModel && (
            <ExAlertBanner
              type={AlertBannerType.WARNING}
              open
              variant={AlertBannerVariant.SUBTLE}
              className="register-model-drawer__warning-banner"
              data-testid="models-list-deprecated-model-warning"
            >
              {t('models_deprecated_model_banner')}
            </ExAlertBanner>
          )}
          {!inline && hasRetiredModel && (
            <ExAlertBanner
              type={AlertBannerType.ERROR}
              open
              variant={AlertBannerVariant.SUBTLE}
              className="register-model-drawer__warning-banner"
              data-testid="models-list-retired-model-warning"
            >
              {t('models_retired_model_banner')}
            </ExAlertBanner>
          )}

          {filteredModels.length > 0 && (useCardView ? (
            <div className="models-list-drawer__cards">
              {filteredModels
                // .slice((selectedPage - 1) * pageSize, selectedPage * pageSize)
                .map((model) => renderModelCard(model))}
            </div>
          ) : (
            <ExTable
              gridOptions={gridOptions}
              data-testid="models-list-table"
              className="models-list-table"
            />
          ))}

          {filteredModels.length === 0 && (
            <div className="models-list-drawer__empty-state">
              <ExEmptyState
                label=""
                text={
                  (searchTerm || '').length > 0
                    ? t('models_no_search_results', { searchTerm })
                    : t('models_no_display')
                }
              >
                <div slot="header">
                  <img alt="Dialog Banner" src={noResultsImage} />
                </div>
              </ExEmptyState>
            </div>
          )}
        </div>
      )}
    </>
  );

  const mainContent = inline ? (
    <div className="models-list-inline">
      <div className="models-list-inline__header">
        {t('models_label', 'Models')}
        {': '}
        {totalRowsRef.current}
      </div>
      {renderContent()}
    </div>
  ) : (
    <div className="models-list-drawer">
      <ExSideDrawer
        panelTitle={`Models: ${totalRowsRef.current}`}
        open={open}
        onClose={() => {
          onClose();
          onParentClose?.();
        }}
        onCancel={onClose}
        data-testid="models-list-side-drawer"
        className={
          fromConfigurationsDrawer
            ? 'models-list-drawer__content models-list-drawer-from-configurations'
            : 'models-list-drawer__content'
        }
        navigation={navigationEnabled}
        preventStacking={fromConfigurationsDrawer ? false : !navigationEnabled}
      >
        {renderContent()}

        <div slot="footer" className="models-list-drawer__footer">
          <ExButton
            type={ButtonType.SECONDARY}
            size={ButtonSize.DEFAULT}
            flavor={ButtonFlavor.BRANDED}
            onClick={handleClose}
            data-testid="models-list-close-btn"
          >
            {t('close')}
          </ExButton>
        </div>
      </ExSideDrawer>

      {!inline && showBoomiModelsDrawer && (
        <ModelAgentsDrawer
          open={showBoomiModelsDrawer}
          modelId={selectedModelForAgents?.model_id || ''}
          modelName={selectedModelForAgents?.display_name || ''}
          modelConfigId={configId || ''}
          provider={selectedModelForAgents?.provider || 'Custom'}
          navigation
          fromConfigurationsDrawer={fromConfigurationsDrawer}
          onClose={() => {
            setShowBoomiModelsDrawer(false);
            setSelectedModelForAgents(null);
          }}
          onParentClose={handleClose}
        />
      )}
    </div>
  );

  return mainContent;
}

ModelsListDrawer.defaultProps = {
  configName: '',
  provider: 'Custom',
  navigationEnabled: false,
  inline: false,
};

export default ModelsListDrawer;
